import { AsyncPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, Input, OnInit, Self } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NavigationEnd, NavigationStart, Router, RouterModule } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionType,
} from '@azure/msal-browser';
import type { LoginDto, LoginTokenStatDto } from '@flexyoo/api/models';
import { AuthenticationService, AuthScheme, hasSameTenantHrAndEmployee } from '@flexyoo/auth';
import { WhiteLabelService } from '@flexyoo/common';
import {
  EmployeePlanInvitationDataAccessService,
  EmployeeSettingsService,
} from '@flexyoo/employee/data-access';
import { IEmployeeSettings } from '@flexyoo/employee/models';
import { UserAgreementService } from '@flexyoo/employee/ui';
import { environment } from '@flexyoo/environment';
import { FiltersStorageService, SidenavStateService } from '@flexyoo/shared/services';
import { GenericPageWithLogoComponent } from '@flexyoo/shared/ui';
import { ITenantSettings } from '@flexyoo/tenant/models';
import { TranslateModule } from '@ngx-translate/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { uniq } from 'lodash';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { getAppRoutePerRoleDefault } from 'src/app/app-routes-per-role-defaults.constant';
import { UserRole } from 'src/app/libs/admin/users/data-access/admin-users.dto';
import { RoleEnum } from 'src/app/shared/models/general/general-enum';
import { NgOnDestroy } from 'src/app/shared/ng-on-destroy.service';
import {
  isCafeteriaPlanEnrolled,
  isMobilityBudgetEnrolled,
} from '../../../libs/employee/util/employee-plans-info.util';
import { User } from '../../../shared/models/user-model';
import { LoginMultiroleComponent } from './login-multirole/login-multirole.component';

export type StartLoginFlow = {
  kind: 'start';
};

export type BeforeStartLoginFlow = {
  kind: 'before-start';
};

export type UserWithToken = {
  user: User;
  accessToken: string;
};

export type UsersLoginResult = {
  userName: string;
  refreshToken: string;
  users: UserWithToken[];
  canSwitchRole: boolean;
};

export type ChooseRoleLoginFlow = {
  kind: 'choose-role';
  roles: UserRole[];
  userName: string;
  result: UsersLoginResult;
};

export type ChooseUserLoginFlow = {
  kind: 'choose-user';
  role: UserRole;
  result: UsersLoginResult;
  chooseRoleFlow: ChooseRoleLoginFlow;
};

export type UserSignAddendumExpiredFlow = {
  kind: 'user-sign-agreement-expired';
};

export type LoginFlow =
  | BeforeStartLoginFlow
  | StartLoginFlow
  | ChooseRoleLoginFlow
  | ChooseUserLoginFlow
  | UserSignAddendumExpiredFlow;

type IFormValue = {
  email: FormControl<string>;
  password: FormControl<string>;
};

const accountScopes = [`api://${environment.ssoClientId}/access`];

@Component({
  selector: 'app-login',
  templateUrl: 'login.component.html',
  styleUrls: ['login.component.css'],
  providers: [NgOnDestroy],
  imports: [
    GenericPageWithLogoComponent,
    TranslateModule,
    ReactiveFormsModule,
    MatError,
    AsyncPipe,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    RouterModule,
    LoginMultiroleComponent,
  ],
})
export class LoginComponent implements OnInit {
  @Input() backToLoginOnly = false;
  public readonly basicAuthFailed$ = new BehaviorSubject<boolean>(false);

  public readonly userAgreementApprovedError$ = new BehaviorSubject<boolean>(false);
  public readonly passwordNotSetError$ = new BehaviorSubject<boolean>(false);

  tenantIsNotActiveError = false;
  employeeIsNotActiveError = false;
  public readonly userNotFoundError$ = new BehaviorSubject<boolean>(false);
  public readonly ssoNotAuthorised$ = new BehaviorSubject<boolean>(false);
  public form: FormGroup<IFormValue>;
  public loginFlow: LoginFlow = { kind: 'before-start' };

  public readonly authScheme$ = this.auth.authScheme$;
  public readonly isLoading$ = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(MSAL_GUARD_CONFIG)
    private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly msalBroadcastService: MsalBroadcastService,
    public readonly auth: AuthenticationService,
    private readonly router: Router,
    private readonly msalService: MsalService,
    @Self() private readonly onDestroy$: NgOnDestroy,
    private readonly sidenavStateService: SidenavStateService,
    private readonly wlc: WhiteLabelService,
    private readonly hrFiltersService: FiltersStorageService<any>,
    private readonly userAgreementService: UserAgreementService,
    private readonly employeeSettingsService: EmployeeSettingsService,
    private readonly employeePlanInvitationService: EmployeePlanInvitationDataAccessService,
    private readonly http: HttpClient,
    private readonly oidcSecurityService: OidcSecurityService
  ) {
    this.resetErrors();
    this.form = new FormGroup({
      email: new FormControl(''),
      password: new FormControl(''),
    });
  }

  protected readonly isNavigating$ = this.router.events.pipe(
    filter((ev) => ev instanceof NavigationStart || ev instanceof NavigationEnd),
    map((ev) => ev instanceof NavigationStart),
    distinctUntilChanged()
  );

  public async ngOnInit() {
    await this.checkUserErrors();

    this.handleMsalRedirectObservable();
    this.handleMsalLogoutSuccess();

    this.msalService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

    this.handleMsalLoginSuccess();

    this.handleOidcIsAuthorized();
  }

  private handleOidcIsAuthorized(): void {
    this.oidcSecurityService
      .checkAuth()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((loginResponse) => {
        if (loginResponse?.isAuthenticated) {
          // Stays on HttpClient: the request carries the portal's access token, which the generated
          // `createSsoGroupsPortal()` has no way to send.
          this.http
            .post<LoginDto>(
              `${environment.api}/auth/sso/groups-portal`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${loginResponse.accessToken}`,
                },
              }
            )
            .pipe(catchError((err) => of(null)))
            .subscribe((resp) => {
              if (!resp) {
                this.router.navigate(['/login']);
                return;
              }
              this.handleTokensList(resp);
            });
        }
      });
  }

  private handleMsalRedirectObservable(): void {
    this.msalService
      .handleRedirectObservable()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        if (
          !this.msalService.instance.getActiveAccount() ||
          !(this.msalService.instance.getAllAccounts().length > 0)
        ) {
          return;
        }

        this.isLoading$.next(true);
        this.msalAcquireTokenSilent();
      });
  }

  private handleMsalLoginSuccess(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ),
        takeUntil(this.onDestroy$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        if (result.eventType === EventType.LOGIN_SUCCESS) {
          this.msalService.instance.setActiveAccount(payload.account);
          this.msalAcquireTokenSilent();
        } else if (!this.msalService.instance.getActiveAccount()) {
          this.msalService.instance.setActiveAccount(payload.account);
        }
      });
  }

  private handleMsalLogoutSuccess(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => this.returnToEmail());
  }

  public chooseAuthSchema(): void {
    this.auth.resetCurrentUser();
    this.wlc.useDefaultTheme$.next(false);
    this.userAgreementApprovedError$.next(false);
    this.employeeIsNotActiveError = false;
    this.tenantIsNotActiveError = false;
    if (!this.form.value.email) {
      return;
    }
    this.isLoading$.next(true);
    this.auth.getAuthSchema(this.form.value.email.trim()).subscribe(
      ({ Scheme, HasPassword }) => {
        this.isLoading$.next(false);
        this.auth.setAuthScheme(Scheme);
        switch (Scheme) {
          case AuthScheme.Basic:
            this.loginFlow = { kind: 'start' };
            break;
          case AuthScheme.GroupS:
            this.signUp();
            break;
          case AuthScheme.GroupSPortal:
            this.oidcSecurityService.authorize();
            return;
        }
        if (!HasPassword) {
          this.passwordNotSetError$.next(true);
        }
      },
      (err) => {
        this.isLoading$.next(false);
        if (err.status === 404) {
          this.userNotFoundError$.next(true);
        }
      }
    );
  }

  public returnToEmail(): void {
    this.auth.setAuthScheme(AuthScheme.GroupS);
    this.loginFlow = { kind: 'before-start' };
    this.resetErrors();
  }

  async signUp() {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      await this.msalService.instance.initialize();
      this.msalService
        .loginPopup({
          scopes: [],
          prompt: 'create',
        })
        .subscribe((response: AuthenticationResult) => {
          this.msalService.instance.setActiveAccount(response.account);

          this.msalAcquireTokenSilent();
        });
    } else {
      this.msalService.loginRedirect({
        scopes: [],
        prompt: 'select_account',
      });
    }
  }

  private msalAcquireTokenSilent(): void {
    this.msalService.instance
      .acquireTokenSilent({
        account: this.msalService.instance.getActiveAccount(),
        scopes: accountScopes,
      })
      .then((resp) => {
        this.auth.setAuthScheme(AuthScheme.GroupS);
        this.isLoading$.next(true);
        this.auth.trySSO(resp.accessToken).subscribe(
          async (result) => {
            this.isLoading$.next(false);
            await this.handleTokensList(result);
          },
          () => {
            this.isLoading$.next(false);
            this.ssoNotAuthorised$.next(true);
          }
        );
      })
      .catch(() => {
        this.isLoading$.next(false);
        this.auth.logout();
      });
  }

  private async loginAs(role: 'hr' | 'employee' | 'opco' | 'admin') {
    const flow = this.loginFlow;
    if (flow.kind !== 'choose-role') {
      return;
    }

    const users = flow.result.users.filter((f) => f.user.Role.toLowerCase() === role);
    if (users.length > 1) {
      this.loginFlow = {
        kind: 'choose-user',
        role: users[0].user.Role,
        chooseRoleFlow: { ...this.loginFlow },
        result: {
          userName: flow.result.userName,
          refreshToken: flow.result.refreshToken,
          users,
          canSwitchRole: flow.result.canSwitchRole,
        },
      } as ChooseUserLoginFlow;
    } else {
      await this.auth
        .setTokens(users[0].accessToken, flow.result.refreshToken, flow.result.canSwitchRole)
        .toPromise();
      await this.finishLogin();
    }
  }

  public async loginAsUser(user: UserWithToken) {
    if (this.loginFlow.kind !== 'choose-user') {
      return;
    }
    await this.auth
      .setTokens(
        user.accessToken,
        this.loginFlow.result.refreshToken,
        this.loginFlow.result.canSwitchRole
      )
      .toPromise();
    await this.finishLogin();
  }

  public back(): void {
    if (this.backToLoginOnly) {
      this.returnToEmail();
      this.router.navigate(['/login']);
      return;
    }
    if (this.loginFlow.kind === 'choose-user' && this.loginFlow.chooseRoleFlow) {
      this.loginFlow = this.loginFlow.chooseRoleFlow;
    } else {
      this.loginFlow = {
        kind: 'start',
      };
    }
  }

  private async finishLogin(loginResponse?: LoginDto) {
    if (loginResponse) {
      const tokenUser = this.auth.userDecode(loginResponse.AccessTokens[0].Token);
      await this.auth
        .setTokens(loginResponse.AccessTokens[0].Token, loginResponse.RefreshToken)
        .toPromise();

      if (tokenUser.Role === UserRole.Employee) {
        const { employeeSettings, tenantSettings } = await this.employeeSettingsService
          .updateAndLoadEmployeeSettings(tokenUser.Id)
          .toPromise();

        if (this.isUserAgreementExpired(employeeSettings, tenantSettings)) {
          this.loginFlow = { kind: 'user-sign-agreement-expired' };
          this.auth.resetCurrentUser();
          return;
        }
      }
    }

    const user = this.auth.getCurrentUser();
    let employeeDefaultRoute: string | null = null;

    if (user.Role === RoleEnum.Employee) {
      this.userAgreementApprovedError$.next(false);

      const { employeeSettings, tenantSettings, userSettings } = await this.employeeSettingsService
        .updateAndLoadEmployeeSettings(user.Id)
        .toPromise();

      this.employeeIsNotActiveError =
        !userSettings?.isActive || !employeeSettings?.isEligible || !tenantSettings?.isActive;

      const invitation = await this.employeePlanInvitationService
        .loadEmployeePlanInvitation(user.EmployeeId)
        .toPromise();

      employeeDefaultRoute = getAppRoutePerRoleDefault(
        RoleEnum.Employee,
        employeeSettings,
        tenantSettings,
        invitation
      );

      const userAgreementState = this.userAgreementService.checkUserAgreementState(
        employeeSettings,
        tenantSettings,
        invitation
      );
      switch (userAgreementState) {
        case 'under-validation': {
          this.userAgreementApprovedError$.next(true);
          this.resetSSOAccount();
          break;
        }
        case 'sign-agreement-expired':
          this.loginFlow = { kind: 'user-sign-agreement-expired' };
          return;
        default: {
          if (
            this.userAgreementService.shouldRedirectToAccountOnLogin(
              employeeSettings,
              tenantSettings,
              invitation
            )
          ) {
            this.router.navigate(['/employee-account']);
            return;
          }
        }
      }
    }

    if (user.Role === RoleEnum.HR) {
      const { tenantSettings, userSettings } = await this.employeeSettingsService
        .updateAndLoadTenantSettings(user.Id)
        .toPromise();

      this.tenantIsNotActiveError = !tenantSettings?.isActive || !userSettings?.isActive;
      this.hrFiltersService.resetUserFilters(user);
    }

    this.authScheme$.pipe(take(1)).subscribe((authScheme) => {
      if (authScheme === AuthScheme.GroupS) {
        this.loginFlow = { kind: 'before-start' };
      } else {
        this.loginFlow = { kind: 'start' };
      }
      const redirectUrl =
        this.auth.failedUrlToRedirect$.value ??
        employeeDefaultRoute ??
        getAppRoutePerRoleDefault(user.Role as RoleEnum);
      this.router.navigate([redirectUrl]);
      this.sidenavStateService.setIsExpanded(true);
    });
  }

  public async submit() {
    this.resetErrors(true);
    this.tenantIsNotActiveError = false;
    this.employeeIsNotActiveError = false;

    if (this.form.valid) {
      try {
        const result = await this.auth
          .tryLogin({
            Email: (this.form.value.email ?? '').trim(),
            Password: this.form.value.password ?? '',
          })
          .toPromise();
        await this.handleTokensList(result);
      } catch (error) {
        this.basicAuthFailed$.next(true);
      }
    }
  }

  public async onRoleSelected(role: string) {
    await this.loginAs(role.toLowerCase() as any);
  }

  protected formatTenantName(user: User): string {
    const displayTenantName =
      (user.IsMultiTenantHr ? user.TenantGroupName : '') || user.TenantShortName || user.TenantName;
    return displayTenantName ? displayTenantName.replace(',', ' ') : displayTenantName;
  }

  public currentYearLong() {
    return new Date().getFullYear();
  }

  public async handleTokensList(result: LoginDto) {
    if (result.AccessTokens.length === 0) {
      this.tenantIsNotActiveError = this.getIsNotActiveErrorValue(
        result.AccessTokenStats,
        RoleEnum.HR
      );
      if (this.tenantIsNotActiveError) {
        return;
      }

      this.employeeIsNotActiveError = this.getIsNotActiveErrorValue(
        result.AccessTokenStats,
        RoleEnum.Employee
      );
      if (this.employeeIsNotActiveError) {
        return;
      }
    }

    if (result.AccessTokens.length > 1) {
      const usersWithToken: UserWithToken[] = result.AccessTokens.map(({ Token }) => ({
        accessToken: Token,
        user: this.auth.userDecode(Token),
      }));
      const userRoles = uniq(usersWithToken.map((user) => user.user.Role));
      const canSwitchRole = hasSameTenantHrAndEmployee(usersWithToken.map(({ user }) => user));

      if (userRoles.length > 1) {
        this.loginFlow = {
          kind: 'choose-role',
          roles: userRoles,
          result: {
            userName: usersWithToken[0].user.UserName,
            refreshToken: result.RefreshToken,
            users: usersWithToken,
            canSwitchRole,
          },
        } as ChooseRoleLoginFlow;
      } else {
        this.loginFlow = {
          kind: 'choose-user',
          role: userRoles[0],
          result: {
            userName: usersWithToken[0].user.UserName,
            refreshToken: result.RefreshToken,
            users: usersWithToken,
            canSwitchRole,
          },
        } as ChooseUserLoginFlow;
      }
    } else {
      await this.finishLogin(result);
    }
  }

  private async checkUserErrors() {
    this.resetErrors();
    const user = this.auth.getCurrentUser();
    if (!user) {
      return;
    }

    if (user.Role === RoleEnum.Employee) {
      const { employeeSettings, tenantSettings, userSettings } = await this.employeeSettingsService
        .updateAndLoadEmployeeSettings(user.Id, true)
        .toPromise();

      const cafeteriaUserAgreementApprovedError =
        isCafeteriaPlanEnrolled(employeeSettings) &&
        tenantSettings.useCafeteriaAgreement &&
        !!employeeSettings.cafeteriaUserAgreement?.AppFileId &&
        !employeeSettings.cafeteriaUserAgreement?.ApprovedDate;

      const mobilityBudgetUserAgreementApprovedError =
        isMobilityBudgetEnrolled(employeeSettings) &&
        tenantSettings.useMobilityBudgetAgreement &&
        !!employeeSettings.mobilityBudgetUserAgreement?.AppFileId &&
        !employeeSettings.mobilityBudgetUserAgreement?.ApprovedDate;

      const userAgreementApprovedError =
        cafeteriaUserAgreementApprovedError || mobilityBudgetUserAgreementApprovedError;
      this.userAgreementApprovedError$.next(userAgreementApprovedError);

      if (userAgreementApprovedError) {
        this.resetSSOAccount();
      }

      this.employeeIsNotActiveError =
        !userSettings?.isActive || !employeeSettings?.isEligible || !tenantSettings?.isActive;

      if (this.isUserAgreementExpired(employeeSettings, tenantSettings)) {
        this.loginFlow = { kind: 'user-sign-agreement-expired' };
      }
    }

    if (user.Role === RoleEnum.HR) {
      const { userSettings, tenantSettings } = await this.employeeSettingsService
        .updateAndLoadTenantSettings(user.Id, true)
        .toPromise();
      this.tenantIsNotActiveError = !userSettings?.isActive || !tenantSettings?.isActive;
    }
  }

  private isUserAgreementExpired(
    employeeSettings: IEmployeeSettings,
    tenantSettings: ITenantSettings
  ): boolean {
    return (
      this.isCafeteriaPlanUserAgreementExpired(employeeSettings, tenantSettings) ||
      this.isMobilityBudgetPlanUserAgreementExpired(employeeSettings, tenantSettings)
    );
  }

  private isCafeteriaPlanUserAgreementExpired(
    employeeSettings: IEmployeeSettings,
    tenantSettings: ITenantSettings
  ): boolean {
    if (!isCafeteriaPlanEnrolled(employeeSettings)) {
      return false;
    }

    if (!tenantSettings.useCafeteriaAgreement) {
      return false;
    }

    if (employeeSettings.cafeteriaUserAgreement?.ApprovedDate) {
      return false;
    }

    if (employeeSettings.cafeteriaUserAgreement?.AppFileId) {
      this.userAgreementApprovedError$.next(true);
      this.resetSSOAccount();
      return false;
    }

    return (
      employeeSettings.userAgreementSignEndDate &&
      new Date(employeeSettings.userAgreementSignEndDate) < new Date()
    );
  }

  private isMobilityBudgetPlanUserAgreementExpired(
    employeeSettings: IEmployeeSettings,
    tenantSettings: ITenantSettings
  ): boolean {
    if (!isMobilityBudgetEnrolled(employeeSettings)) {
      return false;
    }

    if (!tenantSettings.useMobilityBudgetAgreement) {
      return false;
    }

    if (employeeSettings.mobilityBudgetUserAgreement?.ApprovedDate) {
      return false;
    }

    if (employeeSettings.mobilityBudgetUserAgreement?.AppFileId) {
      this.userAgreementApprovedError$.next(true);
      this.resetSSOAccount();
      return false;
    }

    return (
      employeeSettings.userAgreementSignEndDate &&
      new Date(employeeSettings.userAgreementSignEndDate) < new Date()
    );
  }

  private getIsNotActiveErrorValue(stats: LoginTokenStatDto[], role: RoleEnum): boolean {
    const roleStat = stats.find((stat) => stat.UserRole === role);
    const hasRoleInactiveToken = roleStat?.ActiveCount === 0 && roleStat?.TotalCount > 0;

    return hasRoleInactiveToken;
  }
  private resetErrors(preservePasswordNotSetError = false): void {
    this.userAgreementApprovedError$.next(false);
    this.ssoNotAuthorised$.next(false);
    this.userNotFoundError$.next(false);
    this.basicAuthFailed$.next(false);

    if (!preservePasswordNotSetError) {
      this.passwordNotSetError$.next(false);
    }
  }

  private resetSSOAccount(): void {
    this.msalService.instance.setActiveAccount(null);
    this.msalService.instance.clearCache();

    this.wlc.useDefaultTheme$.next(true);
  }
}
